import React, { useCallback, useEffect, useState } from 'react';
import { Download, LogOut, Mail, Megaphone, FileText, LayoutPanelTop, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LayoutBase } from './base';
import HourglassLoader from './ui/HourglassLoader';
import apiClient from '../lib/apiClient';
import storage from '../lib/storage';
import './AdminPanel.css';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: Shield },
  { id: 'emails', label: 'E-mails', icon: Mail },
  { id: 'prompts', label: 'Prompts', icon: FileText },
  { id: 'announcements', label: 'Avisos', icon: Megaphone },
  { id: 'landing', label: 'Landing', icon: LayoutPanelTop }
];

function AdminPanel() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [landing, setLanding] = useState({ headline: '', subheadline: '', main_text: '', cta_text: '' });

  const token = storage.get('adminAuthToken');

  const getAuthHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => {
    storage.remove('adminAuthToken');
    storage.remove('adminEmail');
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const apiSend = async (path, method, body) => {
    const { response, data } = await apiClient.request(path, {
      method,
      auth: false,
      meta: true,
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (response.status === 401) {
      logout();
      throw new Error('Sessão expirada');
    }
    if (!response.ok || !data?.success) throw new Error(data?.message || 'Erro da API admin');
    return data;
  };

  useEffect(() => {
    const loadData = async () => {
      if (!token) {
        navigate('/admin/login', { replace: true });
        return;
      }

      setLoading(true);
      try {
        const [emailsReq, promptsReq, announcementsReq, landingReq] = await Promise.all([
          apiClient.request('/admin/emails?page=1&limit=20', { auth: false, meta: true, headers: getAuthHeaders() }),
          apiClient.request('/admin/prompts', { auth: false, meta: true, headers: getAuthHeaders() }),
          apiClient.request('/admin/announcements', { auth: false, meta: true, headers: getAuthHeaders() }),
          apiClient.request('/admin/landing', { auth: false, meta: true, headers: getAuthHeaders() })
        ]);

        const responses = [emailsReq, promptsReq, announcementsReq, landingReq];
        const hasUnauthorized = responses.some((item) => item.response?.status === 401);
        if (hasUnauthorized) {
          logout();
          return;
        }

        const [emailsData, promptsData, announcementsData, landingData] = responses.map((item) => item.data);
        setEmails(emailsData.items || []);
        setPrompts(promptsData.prompts || []);
        setAnnouncements(announcementsData.items || []);
        setLanding(landingData.landing || { headline: '', subheadline: '', main_text: '', cta_text: '' });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [getAuthHeaders, logout, navigate, token]);

  const exportEmails = async () => {
    const blob = await apiClient.request('/admin/emails/export', {
      responseType: 'blob',
      auth: false,
      headers: getAuthHeaders()
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'emails_marketingpro.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <LayoutBase className="admin-layout">
      <aside className="admin-sidebar card">
        <h2>Painel Admin</h2>
        <nav>
          {TABS.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.id} className={`admin-nav-btn ${tab === item.id ? 'active' : ''}`} onClick={() => setTab(item.id)}>
                <Icon size={14} /> {item.label}
              </button>
            );
          })}
        </nav>
        <button className="btn btn-outline" onClick={logout}><LogOut size={14} /> Sair</button>
      </aside>

      <main className="admin-main">
        {loading && <HourglassLoader variant="inline" message="Carregando painel..." />}

        {!loading && tab === 'dashboard' && (
          <section className="card admin-section">
            <h1>Dashboard Admin</h1>
            <div className="admin-kpis">
              <div className="card"><strong>{emails.length}</strong><span>E-mails</span></div>
              <div className="card"><strong>{prompts.length}</strong><span>Prompts</span></div>
              <div className="card"><strong>{announcements.length}</strong><span>Avisos</span></div>
            </div>
          </section>
        )}

        {!loading && tab === 'emails' && (
          <section className="card admin-section">
            <div className="card-header-action">
              <h1>E-mails</h1>
              <button className="btn btn-outline" onClick={exportEmails}><Download size={14} /> Exportar CSV</button>
            </div>
            <ul className="admin-list">
              {emails.map((item) => <li key={item.id || item.email}>{item.email}</li>)}
            </ul>
          </section>
        )}

        {!loading && tab === 'prompts' && (
          <section className="card admin-section">
            <h1>Prompts</h1>
            <textarea
              rows={10}
              value={JSON.stringify(prompts, null, 2)}
              onChange={(event) => {
                try {
                  setPrompts(JSON.parse(event.target.value));
                } catch (error) {
                  console.error('JSON inválido');
                }
              }}
            />
            <button className="btn btn-outline" onClick={() => apiSend('/admin/prompts/update', 'POST', { prompts })}>Salvar prompts</button>
          </section>
        )}

        {!loading && tab === 'announcements' && (
          <section className="card admin-section">
            <h1>Avisos</h1>
            <ul className="admin-list">
              {announcements.map((item) => <li key={item.id}>{item.title || item.message}</li>)}
            </ul>
          </section>
        )}

        {!loading && tab === 'landing' && (
          <section className="card admin-section">
            <h1>Landing</h1>
            <div className="input-group"><label>Headline</label><input value={landing.headline || ''} onChange={(event) => setLanding((prev) => ({ ...prev, headline: event.target.value }))} /></div>
            <div className="input-group"><label>Subheadline</label><input value={landing.subheadline || ''} onChange={(event) => setLanding((prev) => ({ ...prev, subheadline: event.target.value }))} /></div>
            <div className="input-group"><label>Texto principal</label><textarea rows={6} value={landing.main_text || ''} onChange={(event) => setLanding((prev) => ({ ...prev, main_text: event.target.value }))} /></div>
            <div className="input-group"><label>CTA</label><input value={landing.cta_text || ''} onChange={(event) => setLanding((prev) => ({ ...prev, cta_text: event.target.value }))} /></div>
            <button className="btn btn-outline" onClick={() => apiSend('/admin/landing/update', 'POST', landing)}>Salvar landing</button>
          </section>
        )}
      </main>
    </LayoutBase>
  );
}

export default AdminPanel;
